import PropTypes from 'prop-types';
import { Action } from 'redux';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';

import { PrimeError } from '../../types';
import { ConfigBase } from '../../types/config';
import ApiHelper from '../ApiHelper';
import { AuthState } from '../auth/Types';
import * as Actions from './Actions';
import { ErrorBoundaryError, ErrorBoundaryState } from './Types';

type RootSubState = {
    authState: AuthState,
    errorBoundaryState: ErrorBoundaryState,
};

type ErrorBoundaryThunkAction<R> = ThunkAction<R, RootSubState, undefined, Action>;
type ErrorBoundaryThunkDispatch = ThunkDispatch<RootSubState, undefined, Action>;

export type ServiceActions = {
    handleError: (error: Error, info: any) => Promise<void>,
};

export const ServiceActionPropTypes = PropTypes.shape({
    handleError: PropTypes.func.isRequired,
}).isRequired;

export const createServiceActions = (config: ConfigBase) => {
    const apiHelper = ApiHelper(config);

    const handleError = (error: Error, info: any): ErrorBoundaryThunkAction<Promise<void>> => {
        return async (dispatch, getState) => {
            try {
                const errorBoundaryError: ErrorBoundaryError = {
                    message: error.message,
                    stack: error.stack || null,
                    info: error instanceof PrimeError ? error.description : info,
                    showGeneric: error instanceof PrimeError ? error.showGeneric : true,
                };
                dispatch(Actions.setError(errorBoundaryError));
                const data = {
                    error: JSON.stringify({
                        message: error.message,
                        stack: error.stack || null,
                    }),
                    info: JSON.stringify(info),
                    state: JSON.stringify(getState()), // in runtime, this will actually dump the entire redux state, which is exactly what we want
                };

                dispatch(Actions.sendErrorRequest());
                await dispatch(apiHelper.post(`/api/${config.ResourceCode}/app/client-error-info`, data));
                dispatch(Actions.sendErrorSuccess());
            } catch (error) {
                dispatch(Actions.sendErrorFailure());
                throw error;
            }
        };
    };

    return {
        handleError,
    };
};

export const bindServiceActionCreators = (config: ConfigBase, dispatch: ErrorBoundaryThunkDispatch): ServiceActions => {
    const services = createServiceActions(config);
    return {
        handleError: (error: Error, info: any) => dispatch(services.handleError(error, info)),
    };
};
